/**
 * Shopify Custom Cart Page JavaScript
 * Handles tab navigation, sticky checkout bar behavior, and variant selection
 */

(function() {
  'use strict';

  // ===== Tab Navigation Handler =====
  class CartTabNavigation {
    constructor() {
      this.tabs = document.querySelectorAll('.cart-tabs__tab');
      this.init();
    }

    init() {
      this.tabs.forEach(tab => {
        tab.addEventListener('click', (e) => this.handleTabClick(e));
      });
    }

    handleTabClick(event) {
      const clickedTab = event.currentTarget;
      const tabName = clickedTab.dataset.tab;

      // Remove active class from all tabs
      this.tabs.forEach(tab => {
        tab.classList.remove('cart-tabs__tab--active');
      });

      // Add active class to clicked tab
      clickedTab.classList.add('cart-tabs__tab--active');

      // Trigger custom event for page to handle tab switching
      document.dispatchEvent(new CustomEvent('cartTabChanged', {
        detail: { tab: tabName }
      }));
    }

    setActiveTab(tabName) {
      this.tabs.forEach(tab => {
        if (tab.dataset.tab === tabName) {
          tab.classList.add('cart-tabs__tab--active');
        } else {
          tab.classList.remove('cart-tabs__tab--active');
        }
      });
    }
  }

  // ===== Sticky Checkout Bar Handler =====
  class StickyCheckoutBar {
    constructor() {
      this.checkoutBar = document.querySelector('.cart-sticky-checkout');
      this.checkoutButton = document.querySelector('.cart-sticky-checkout__button');
      this.itemCountElement = document.querySelector('.cart-sticky-checkout__item-count');

      if (this.checkoutBar) {
        this.init();
      }
    }

    init() {
      // Handle checkout button click
      if (this.checkoutButton) {
        this.checkoutButton.addEventListener('click', (e) => this.handleCheckout(e));
      }

      // Update item count on page load
      this.updateItemCount();

      // Listen for cart updates
      document.addEventListener('cart:updated', () => {
        this.updateItemCount();
      });
    }

    handleCheckout(event) {
      // Prevent default if needed
      if (event.target.tagName === 'BUTTON') {
        event.preventDefault();

        // Navigate to checkout
        const checkoutUrl = this.checkoutButton.href || '/checkout';
        window.location.href = checkoutUrl;
      }
    }

    updateItemCount() {
      const cartForm = document.querySelector('form[action="/cart"]');
      if (!cartForm) return;

      const inputs = cartForm.querySelectorAll('input[name^="updates"]');
      let totalItems = 0;

      inputs.forEach(input => {
        const quantity = parseInt(input.value) || 0;
        totalItems += quantity;
      });

      if (this.itemCountElement) {
        this.itemCountElement.textContent = totalItems;
      }

      // Disable checkout button if cart is empty
      if (totalItems === 0) {
        this.checkoutButton.classList.add('cart-sticky-checkout__button--disabled');
        this.checkoutButton.disabled = true;
      } else {
        this.checkoutButton.classList.remove('cart-sticky-checkout__button--disabled');
        this.checkoutButton.disabled = false;
      }
    }

    show() {
      if (this.checkoutBar) {
        this.checkoutBar.style.display = 'block';
      }
    }

    hide() {
      if (this.checkoutBar) {
        this.checkoutBar.style.display = 'none';
      }
    }
  }

  // ===== Variant Selection Handler =====
  class VariantSelector {
    constructor() {
      this.variantSelects = document.querySelectorAll('.cart-item__variant-select');
      this.init();
    }

    init() {
      this.variantSelects.forEach(select => {
        select.addEventListener('change', (e) => this.handleVariantChange(e));
      });
    }

    handleVariantChange(event) {
      const select = event.currentTarget;
      const cartItem = select.closest('.cart-item');
      const variantId = select.value;
      const itemKey = cartItem.dataset.itemKey || select.dataset.itemKey;

      if (!variantId || !itemKey) {
        console.error('Missing variant ID or item key');
        return;
      }

      // Get current quantity
      const quantityInput = cartItem.querySelector('.cart-item__quantity-input');
      const currentQuantity = parseInt(quantityInput?.value) || 1;

      // Prepare cart update data
      const updates = {};

      // Remove old variant (set to 0)
      updates[itemKey] = 0;

      // Add new variant with same quantity
      updates[variantId] = currentQuantity;

      // Submit cart update
      this.updateCart(updates);
    }

    updateCart(updates) {
      const formData = new FormData();
      Object.keys(updates).forEach(key => {
        formData.append(`updates[${key}]`, updates[key]);
      });

      fetch('/cart/update.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => response.json())
      .then(data => {
        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        // Reload page or update UI as needed
        location.reload();
      })
      .catch(error => {
        console.error('Error updating cart:', error);
        alert('Sorry, there was an error updating your cart. Please try again.');
      });
    }
  }

  // ===== Quantity Stepper Handler =====
  class QuantityStepper {
    constructor() {
      this.decreaseButtons = document.querySelectorAll('[data-action="decrease-quantity"]');
      this.increaseButtons = document.querySelectorAll('[data-action="increase-quantity"]');
      this.quantityInputs = document.querySelectorAll('.cart-item__quantity-input');
      this.init();
    }

    init() {
      this.decreaseButtons.forEach(btn => {
        btn.addEventListener('click', (e) => this.handleDecreaseClick(e));
      });

      this.increaseButtons.forEach(btn => {
        btn.addEventListener('click', (e) => this.handleIncreaseClick(e));
      });

      this.quantityInputs.forEach(input => {
        input.addEventListener('change', (e) => this.handleQuantityChange(e));
      });
    }

    handleDecreaseClick(event) {
      event.preventDefault();
      const button = event.currentTarget;
      const input = button.parentElement.querySelector('.cart-item__quantity-input');
      if (input) {
        let quantity = parseInt(input.value) || 1;
        quantity = Math.max(0, quantity - 1);
        input.value = quantity;
        this.submitQuantityUpdate(input);
      }
    }

    handleIncreaseClick(event) {
      event.preventDefault();
      const button = event.currentTarget;
      const input = button.parentElement.querySelector('.cart-item__quantity-input');
      if (input) {
        let quantity = parseInt(input.value) || 1;
        quantity = Math.min(999, quantity + 1);
        input.value = quantity;
        this.submitQuantityUpdate(input);
      }
    }

    handleQuantityChange(event) {
      const input = event.currentTarget;
      let quantity = parseInt(input.value) || 0;

      // Validate quantity
      if (quantity < 0) quantity = 0;
      if (quantity > 999) quantity = 999;

      input.value = quantity;

      if (quantity === 0) {
        // Ask for confirmation before removing item
        if (confirm('Remove this item from your cart?')) {
          this.submitQuantityUpdate(input);
        } else {
          input.value = 1;
        }
      } else {
        this.submitQuantityUpdate(input);
      }
    }

    submitQuantityUpdate(input) {
      const cartItem = input.closest('.cart-item');
      const itemKey = cartItem.dataset.itemKey;
      const quantity = parseInt(input.value) || 0;

      if (!itemKey) {
        console.error('Missing item key');
        return;
      }

      const formData = new FormData();
      formData.append(`updates[${itemKey}]`, quantity);

      fetch('/cart/update.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => response.json())
      .then(data => {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        location.reload();
      })
      .catch(error => {
        console.error('Error updating quantity:', error);
        alert('Sorry, there was an error updating your cart. Please try again.');
      });
    }
  }

  // ===== Delete Item Handler =====
  class DeleteItemHandler {
    constructor() {
      this.deleteButtons = document.querySelectorAll('[data-action="delete-item"]');
      this.init();
    }

    init() {
      this.deleteButtons.forEach(btn => {
        btn.addEventListener('click', (e) => this.handleDeleteClick(e));
      });
    }

    handleDeleteClick(event) {
      event.preventDefault();

      const button = event.currentTarget;
      const cartItem = button.closest('.cart-item');
      const itemKey = cartItem?.dataset.itemKey;

      if (!itemKey) {
        console.error('Missing item key');
        return;
      }

      if (!confirm('Remove this item from your cart?')) {
        return;
      }

      const formData = new FormData();
      formData.append(`updates[${itemKey}]`, 0);

      fetch('/cart/update.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => response.json())
      .then(data => {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        location.reload();
      })
      .catch(error => {
        console.error('Error deleting item:', error);
        alert('Sorry, there was an error removing this item. Please try again.');
      });
    }
  }

  // ===== Initialize on DOM Ready =====
  function initializeCartPage() {
    // Initialize all components
    const tabNav = new CartTabNavigation();
    const stickyCheckout = new StickyCheckoutBar();
    const variantSelector = new VariantSelector();
    const quantityStepper = new QuantityStepper();
    const deleteHandler = new DeleteItemHandler();
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCartPage);
  } else {
    initializeCartPage();
  }

})();
